import { useMutation, useQuery } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { getIntl } from '../../utils/intl';
import { appAPI } from '../../utils/api';
import {
    getOrganizationUserCacheKey,
    getOrganizationUserGroupsCacheKey,
    getOrganizationUsersCacheKey,
    longTermCacheStaleTime,
    onUpdateOrganizationUsers,
} from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import { useOrganizationId } from './useOrganization';

const intl = getIntl('userOrganizationUsers', {
    usersLoadFailure: 'Failed to load users',
    updateUserSuccess: 'User updated successfully',
    updateUserFailure: 'Failed to update user',
    deleteUserSuccess: 'User deleted successfully',
    deleteUserFailure: 'Failed to delete user',
    deleteUsersSuccess: 'Users deleted successfully',
    deleteUsersFailure: 'Failed to delete users',
    userLoadFailure: 'Failed to load user',
    userGroupsLoadFailure: 'Failed to load user groups',
});

export type UseOrganizationUsersArgs = {
    onUpdateUser?: () => void;
    onDeleteUserSuccess?: () => void;
    onDeleteUsersSuccess?: () => void;
    organizationUserId?: string;
};

export type UpdateOrganizationUserArgs = {
    organizationUserId: string;
    updateRole?: APITypes.organizationService.UpdateOrganizationUserRoleRequest;
    updateBillingAdmin?: APITypes.organizationService.UpdateOrganizationUserIsBillingAdminRequest;
    updateGroups?: APITypes.organizationService.UpdateOrganizationUserOrganizationGroupsRequest;
};

export type UseOrganizationUsersReturnValue = {
    users: APITypes.organizationService.OrganizationUserDetails[];
    isLoadingUsers: boolean;
    isFetchingUsers: boolean;
    updateUser: (args: UpdateOrganizationUserArgs) => void;
    isUpdatingUser: boolean;
    deleteUser: (organizationUserId: string) => void;
    isDeletingUser: boolean;
    deleteUsers: (organizationUserIds: string[]) => void;
    isDeletingUsers: boolean;
    user: APITypes.organizationService.GetOrganizationUserResponse | null;
    isLoadingUser: boolean;
    userGroups: APITypes.organizationService.ListOrganizationUserOrganizationGroupsResponse['items'];
    isLoadingUserGroups: boolean;
};

const emptyArray: any[] = [];

export default function useOrganizationUsers({
    onUpdateUser,
    onDeleteUserSuccess,
    onDeleteUsersSuccess,
    organizationUserId,
}: UseOrganizationUsersArgs): UseOrganizationUsersReturnValue {
    const organizationId = useOrganizationId();
    const notify = useNotify();
    const organizationUsersQuery = useQuery({
        queryKey: getOrganizationUsersCacheKey(),
        queryFn: async () => {
            return await appAPI.get<APITypes.organizationService.ListOrganizationUsersResponse>(
                urls.api.organizations.users.users(organizationId),
            );
        },
        staleTime: longTermCacheStaleTime,
    });
    useQueryErrorNotify(organizationUsersQuery, intl('usersLoadFailure'));

    const organizationUserQuery = useQuery({
        queryKey: organizationUserId ? getOrganizationUserCacheKey(organizationUserId) : [],
        queryFn: async () => {
            const value = await appAPI.get<APITypes.organizationService.GetOrganizationUserResponse>(
                urls.api.organizations.users.user(organizationId, organizationUserId!),
            );

            return value;
        },
        enabled: !!organizationUserId,
    });
    useQueryErrorNotify(organizationUserQuery, intl('userLoadFailure'));

    const organizationUserGroupsQuery = useQuery({
        queryKey: organizationUserId ? getOrganizationUserGroupsCacheKey(organizationUserId) : [],
        queryFn: async () => {
            return await appAPI.get<APITypes.organizationService.ListOrganizationUserOrganizationGroupsResponse>(
                urls.api.organizations.users.userGroups(organizationId, organizationUserId!),
            );
        },
        enabled: !!organizationUserId,
    });
    useQueryErrorNotify(organizationUserGroupsQuery, intl('userGroupsLoadFailure'));

    const updateUserMutation = useMutation({
        mutationFn: async (args: UpdateOrganizationUserArgs) => {
            const updateRole = async (update: APITypes.organizationService.UpdateOrganizationUserRoleRequest) => {
                const body: APITypes.organizationService.UpdateOrganizationUserRoleRequest = {
                    role: update.role,
                };
                return await appAPI.put(urls.api.organizations.users.user(organizationId, args.organizationUserId), {
                    body,
                });
            };

            const updateBillingAdmin = async (
                update: APITypes.organizationService.UpdateOrganizationUserIsBillingAdminRequest,
            ) => {
                const body: APITypes.organizationService.UpdateOrganizationUserIsBillingAdminRequest = {
                    isBillingAdmin: update.isBillingAdmin,
                };
                return await appAPI.put(urls.api.organizations.users.user(organizationId, args.organizationUserId), {
                    body,
                });
            };

            const updateGroups = async (
                update: APITypes.organizationService.UpdateOrganizationUserOrganizationGroupsRequest,
            ) => {
                const body: APITypes.organizationService.UpdateOrganizationUserOrganizationGroupsRequest = {
                    organizationGroupIds: update.organizationGroupIds,
                };
                return await appAPI.put(
                    urls.api.organizations.users.userGroups(organizationId, args.organizationUserId),
                    {
                        body,
                    },
                );
            };

            if (args.updateRole) {
                await updateRole(args.updateRole);
            }

            if (args.updateBillingAdmin) {
                await updateBillingAdmin(args.updateBillingAdmin);
            }

            if (args.updateGroups) {
                await updateGroups(args.updateGroups);
            }
        },
        onSuccess: () => {
            onUpdateOrganizationUsers.resetCache({})();
            notify('success', intl('updateUserSuccess'));
            onUpdateUser?.();
        },
        onError: () => {
            notify('error', intl('updateUserFailure'));
        },
    });

    const deleteUserMutation = useMutation({
        mutationFn: async (organizationUserId: string) => {
            return await appAPI.del(urls.api.organizations.users.user(organizationId, organizationUserId));
        },
        onSuccess: () => {
            onUpdateOrganizationUsers.resetCache({})();
            notify('success', intl('deleteUserSuccess'));
            onDeleteUserSuccess?.();
        },
        onError: () => {
            notify('error', intl('deleteUserFailure'));
        },
    });

    const deleteUsersMutation = useMutation({
        mutationFn: async (organizationUserIds: string[]) => {
            for (const organizationUserId of organizationUserIds) {
                await appAPI.del(urls.api.organizations.users.user(organizationId, organizationUserId));
            }
        },
        onSuccess: () => {
            onUpdateOrganizationUsers.resetCache({})();
            notify('success', intl('deleteUsersSuccess'));
            onDeleteUsersSuccess?.();
        },
        onError: () => {
            notify('error', intl('deleteUsersFailure'));
        },
    });

    return {
        users: organizationUsersQuery.data?.items || emptyArray,
        isLoadingUsers: organizationUsersQuery.isLoading,
        isFetchingUsers: organizationUsersQuery.isFetching,
        updateUser: updateUserMutation.mutate,
        isUpdatingUser: updateUserMutation.isPending,
        deleteUser: deleteUserMutation.mutate,
        isDeletingUser: deleteUserMutation.isPending,
        deleteUsers: deleteUsersMutation.mutate,
        isDeletingUsers: deleteUsersMutation.isPending,
        user: organizationUserQuery.data || null,
        isLoadingUser: organizationUserQuery.isLoading,
        userGroups: organizationUserGroupsQuery.data?.items || emptyArray,
        isLoadingUserGroups: organizationUserGroupsQuery.isLoading,
    };
}
